import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { AgeGroup } from '../../types/api-responses';
import styles from './AgeGroupChip.module.css';

/**
 * AB#1847 (PBI-I) / AB#1838 — Customer-side age-group matrix.
 *
 * Shared chip used wherever Devanza renders the age axis of a service.
 * Bilingual via `t()`. RTL handled via logical properties in the CSS
 * (no physical margins, no class-name flipping).
 *
 * The BE serializes `AgeGroup` as a STRING per Faris F-Q2=A:
 * `"Adult" | "Kid" | "Both"`. We accept the string shape directly and
 * tolerate `null`/`undefined`/unknown values defensively (returns null
 * render instead of crashing on a missing column from a legacy row).
 *
 * Per founder Q-decision (2026-05-31): `Adult` is the silent default —
 * Devanza serves adult bookings as the baseline, so we do NOT render
 * the chip for Adult to keep service cards visually quiet. Only `Kid`
 * and `Both` render as chips next to the gender chip.
 *
 * AR vocabulary per wiki §3 / Q1 decision:
 *   - Kid  → أطفال
 *   - Both → كلاهما   (NOT "الجميع" — that string belongs to the gender
 *     axis "Everyone" label; using it here would overload meaning)
 *
 * i18n keys (added to LanguageContext inline translations):
 *   - services.ageGroup.cardKids   → "Kids" / "أطفال"
 *   - services.ageGroup.cardBoth   → "Both" / "كلاهما"
 *   - services.ageGroup.aria.kids  → "For kids" / "للأطفال"
 *   - services.ageGroup.aria.both  → "For all ages" / "لجميع الأعمار"
 */

export interface AgeGroupChipProps {
    /** Canonical AgeGroup value from the BE. Null/undefined = no chip. */
    ageGroup: AgeGroup | null | undefined;
    /** Optional class override for the wrapping `<span>`. */
    className?: string;
}

/**
 * Defensive: returns the canonical AgeGroup for valid input, else null.
 * Mirrors the shape of `normalizeServiceType` in GenderGlyph.
 */
export function normalizeAgeGroup(
    raw: AgeGroup | string | null | undefined,
): AgeGroup | null {
    if (raw === 'Adult' || raw === 'Kid' || raw === 'Both') return raw;
    return null;
}

const AgeGroupChip: React.FC<AgeGroupChipProps> = ({ ageGroup, className }) => {
    const { t } = useLanguage();

    const normalised = normalizeAgeGroup(ageGroup);
    // `Adult` is the silent default — no chip. `null` (missing data) also
    // produces no chip. Only `Kid` and `Both` render.
    if (!normalised || normalised === 'Adult') {
        return null;
    }

    let glyph: string;
    let labelKey: string;
    let ariaKey: string;
    let variantClass: string;

    switch (normalised) {
        case 'Kid':
            glyph = '👦';
            labelKey = 'services.ageGroup.cardKids';
            ariaKey = 'services.ageGroup.aria.kids';
            variantClass = styles.chipKid;
            break;
        case 'Both':
            glyph = '👦👩';
            labelKey = 'services.ageGroup.cardBoth';
            ariaKey = 'services.ageGroup.aria.both';
            variantClass = styles.chipBoth;
            break;
    }

    return (
        <span
            className={`${styles.chip} ${variantClass} ${className ?? ''}`.trim()}
            aria-label={t(ariaKey)}
            role="img"
        >
            <span aria-hidden="true" className={styles.glyph}>{glyph}</span>
            <span>{t(labelKey)}</span>
        </span>
    );
};

export default AgeGroupChip;
